import type { IUserStatesInEnrollment } from '../../hooks/use-get-user-states-in-enrollment';
import { UserFlowState } from '../../hooks/use-get-user-states-in-enrollment/use-get-user-flow-state';
import { convertDotcmsResponseToRecord } from './converters-from-dotcms';
import type { IBaseObjectData } from './dotcms-base-object';
import { buildGqlFieldsQuery } from './gql-builders';
import type { IGqlHelper } from './gql-helper';

export interface IModelBasedOnEnrollmentStatus<T> extends IBaseObjectData {
  defaultData: T;
  ifEnrollmentIsAvailableData?: T;
  ifInEsppButNotInCashless?: T;
  ifEnrollmentIsClosedData?: T;
  ifAlreadyEnrolledData?: T;
  // Currently it is skipped in dotcms
  ifReadyToAddCashlessMatch?: T;
}

const buildGqlFieldsQueryOrNothing = <T extends IBaseObjectData>(
  helperForData: IGqlHelper<T>,
) => {
  const fieldsQuery = buildGqlFieldsQuery(helperForData);
  return fieldsQuery ? `{ ${fieldsQuery} }` : '';
};

export const buildBasedOnEnrollmentStatusGqlHelper = <
  T extends IBaseObjectData,
>(
    contentTypeName: string,
    helperForData: IGqlHelper<T>,
  ): IGqlHelper<IModelBasedOnEnrollmentStatus<T>> => ({
    contentTypeName,
    gqlFieldsQuery: `
      defaultData ${buildGqlFieldsQueryOrNothing(helperForData)}
      ifEnrollmentIsOpen ${buildGqlFieldsQueryOrNothing(helperForData)}
      ifInEsppButNotInCashless ${buildGqlFieldsQueryOrNothing(helperForData)}
      ifEnrollmentIsClosed ${buildGqlFieldsQueryOrNothing(helperForData)}
      ifAlreadyEnrolled ${buildGqlFieldsQueryOrNothing(helperForData)}
    `,
    processGqlData: (gqlResponse: unknown) => {
      if (!gqlResponse) {
        return undefined;
      }

      const {
        defaultData,
        ifEnrollmentIsOpen,
        ifInEsppButNotInCashless,
        ifEnrollmentIsClosed,
        ifAlreadyEnrolled,
      } = convertDotcmsResponseToRecord(gqlResponse);
      const defaultDataFromResponse = helperForData.processGqlData(defaultData);
      if (!defaultDataFromResponse) {
        return undefined;
      }

      return {
        defaultData: defaultDataFromResponse,
        ifEnrollmentIsAvailableData:
        helperForData.processGqlData(ifEnrollmentIsOpen),
        ifInEsppButNotInCashless: helperForData.processGqlData(
          ifInEsppButNotInCashless,
        ),
        ifEnrollmentIsClosedData:
        helperForData.processGqlData(ifEnrollmentIsClosed),
        ifAlreadyEnrolledData: helperForData.processGqlData(ifAlreadyEnrolled),
      };
    },
  });

export const getDataBasedOnEnrollmentStatus = <T>(
  data: IModelBasedOnEnrollmentStatus<T>,
  userStates: IUserStatesInEnrollment | undefined,
  isEnrollmentAvailable: boolean,
): T | undefined => {
  // If the data is externally set to default values only, that means the data is not really
  // related to the enrollment status and we need to use this default data for all states.
  // If not, and some data is set to specific enrollment states,
  // then we need to use undefined as the default data.
  const isBasedOnState =
    data.ifEnrollmentIsAvailableData !== undefined ||
    data.ifEnrollmentIsClosedData !== undefined ||
    data.ifAlreadyEnrolledData !== undefined ||
    data.ifInEsppButNotInCashless !== undefined;

  if (
    isBasedOnState &&
    ((userStates?.userFlowState.state ===
      UserFlowState.NoEnrollmentActionsAvailable &&
      isEnrollmentAvailable) ||
      userStates?.userFlowState.state ===
        UserFlowState.NoEnrollmentActionsAvailableDueToIneligible)
  ) {
    return undefined;
  }

  const defaultData = isBasedOnState ? undefined : data.defaultData;
  if (userStates && isEnrollmentAvailable) {
    switch (userStates?.userFlowState.state) {
      case UserFlowState.EnrollmentCompleted:
        return data.ifAlreadyEnrolledData ?? defaultData;
      case UserFlowState.ReadyToAddCashlessParticipation:
        return data.ifInEsppButNotInCashless ?? defaultData;
      case UserFlowState.ReadyToAddCashlessMatch:
        return (
          data.ifReadyToAddCashlessMatch ??
          data.ifEnrollmentIsAvailableData ??
          defaultData
        );
      case UserFlowState.ReadyToEnroll:
      default:
        return data.ifEnrollmentIsAvailableData ?? defaultData;
    }
  } else {
    return data.ifEnrollmentIsClosedData ?? defaultData;
  }
};
